import { useState, useCallback } from 'react'
import { mapTracksStoreToAudioForm } from '../features/clips/domain/mappers/clips-to-audio-form/map-tracks-store-to-audioform.ts'
import useTracksStore from '../shared/store/tracks.store.tsx'
import { generateAudioform } from '../features/audio-form/domain/use-cases/generate-audioform.ts'
import { patchSession } from '@/features/session/domain/use-cases/patch-session.ts'
import { useAppStore } from '@/shared/store/app.store.ts'
import { createSession } from '@/features/session/domain/use-cases/create-session.ts'
import { useTracking } from '@/providers/TrackingProvider.tsx'
import { useTranslation } from 'react-i18next'
import { WorkflowId } from '@/models'

export const useApplyMastering = (): {
  applyMastering: (audioFormId?: string) => Promise<void>
  loading: boolean
  error: Error | undefined
} => {
  const [loading, setLoading] = useState(false)
  const [error, setError] = useState<Error>()
  const session = useAppStore((state) => state.session)
  const { trackEvent } = useTracking()
  const { t } = useTranslation()

  const applyMastering = useCallback(async () => {
    trackEvent('ProEditor - Clicked produce audio', {
      sessionId: session?.sessionId,
      projectId: session?.projectId,
      hasExistingSession: !!session,
    })
    setLoading(true)
    setError(undefined)

    const audioFormInput = mapTracksStoreToAudioForm(useTracksStore.getState())

    try {
      const audioForm = await generateAudioform(
        audioFormInput,
        session?.sessionId ?? t('ls_session_not_generated_id')
      )

      // if there's no session, we create project + session
      // if there's a session with other workflowId than "Editor", we create a new session for Editor in the same project
      // if there's a session with "Editor" workflowId, we update it
      if (session?.workflowId === WorkflowId.PRO_EDITOR) {
        void patchSession(audioForm.id)
      } else {
        void createSession(audioForm.id, session?.projectId)
      }

      useTracksStore.getState().setGeneratedMix(audioForm)
    } catch (e: unknown) {
      if (e instanceof Error) {
        console.error('Error applying mastering', e)
        setError(e)
      }
    } finally {
      setLoading(false)
    }
  }, [session, trackEvent])

  return {
    applyMastering,
    loading,
    error,
  }
}
